/**
 * Froala stamps `tabindex="-1"` on every toolbar button it renders, so Bold,
 * Italic, Link, Emoji and AI Assistant are unreachable by keyboard in EVERY
 * rich-text field in the app (measured 2026-07-31: 38 of 38 buttons at
 * tabindex="-1"; 60 consecutive real Tab presses never landed in a toolbar).
 *
 * This is the single shared primitive that repairs it (G-118): it promotes the
 * toolbar's command buttons back into the document tab order and keeps them
 * there as Froala re-renders dropdown popups.
 *
 * It deliberately leaves Froala's own `data-cmd="emptyToolbar"` spacer button
 * out of the tab order -- it is hidden chrome, not a control.
 */

export const SKIP_COMMANDS = ["emptyToolbar"];

/** True when this button is a real, focusable toolbar control. */
export function isFocusableToolbarButton(button) {
  if (!button) return false;
  if (button.tagName !== "BUTTON") return false;
  const cmd = button.getAttribute("data-cmd");
  if (cmd && SKIP_COMMANDS.includes(cmd)) return false;
  return true;
}

/** Put every command button in `toolbar` into the tab order. Returns the count changed. */
export function promoteToolbarButtons(toolbar) {
  if (!toolbar || typeof toolbar.querySelectorAll !== "function") return 0;
  let changed = 0;
  toolbar.querySelectorAll("button").forEach((button) => {
    if (!isFocusableToolbarButton(button)) return;
    if (button.getAttribute("tabindex") === "0") return;
    button.setAttribute("tabindex", "0");
    changed += 1;
  });
  return changed;
}

/**
 * Promote now and on every subsequent Froala re-render.
 * Returns a disposer so the caller can stop observing on unmount.
 */
export function makeToolbarKeyboardReachable(toolbar, ObserverCtor) {
  if (!toolbar) return () => {};
  promoteToolbarButtons(toolbar);
  const Ctor =
    ObserverCtor ||
    (typeof MutationObserver !== "undefined" ? MutationObserver : null);
  if (!Ctor) return () => {};
  const observer = new Ctor(() => promoteToolbarButtons(toolbar));
  observer.observe(toolbar, { childList: true, subtree: true });
  return () => observer.disconnect();
}
